import { AlertCircle, RefreshCw, Inbox } from 'lucide-react';
import { MotionButton, cn } from '@iheart/ui';
import type { ReactNode } from 'react';

/**
 * Full-height centered loading state with the brand spinner.
 * Drop into any page while data is being fetched.
 */
export function LoadingState({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center p-8">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="size-7 animate-spin rounded-full border-[2.5px] border-pink-200 border-t-pink-500" />
        <p className="text-sm text-neutral-400">{message}</p>
      </div>
    </div>
  );
}

/**
 * Error state with retry button.
 * Shows a friendly message and lets the user retry the failed operation.
 */
export function ErrorState({
  message = 'Something went wrong',
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center p-8">
      <div className="flex max-w-xs flex-col items-center gap-3 text-center">
        <div className="flex size-12 items-center justify-center rounded-xl bg-red-50">
          <AlertCircle className="size-6 text-red-500" />
        </div>
        <p className="text-sm font-medium text-neutral-700">{message}</p>
        <p className="text-xs text-neutral-400">
          Check your connection and try again.
        </p>
        {onRetry && (
          <MotionButton size="sm" variant="secondary" onClick={onRetry}>
            <RefreshCw className="mr-1.5 size-3.5" /> Try again
          </MotionButton>
        )}
      </div>
    </div>
  );
}

/**
 * Empty state placeholder for lists/tables with no data.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title = 'Nothing here yet',
  description,
  action,
}: {
  icon?: typeof Inbox;
  title?: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-dashed border-white/40 bg-white/30 p-8 backdrop-blur-sm">
      <div className="flex max-w-xs flex-col items-center gap-2 text-center">
        <Icon className="size-8 text-neutral-300" />
        <p className="text-sm font-medium text-neutral-600">{title}</p>
        {description && (
          <p className="text-xs text-neutral-400">{description}</p>
        )}
        {action && (
          <MotionButton size="sm" className="mt-1" onClick={action.onClick}>
            {action.label}
          </MotionButton>
        )}
      </div>
    </div>
  );
}

/* ── Skeleton loading primitives ──────────────────────────────── */

/**
 * A single shimmer block. Use for building custom skeleton layouts.
 *
 * @example
 * <Skeleton className="h-4 w-32" />           // text line
 * <Skeleton className="size-10 rounded-full" /> // avatar
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded bg-neutral-200/60',
        className,
      )}
    />
  );
}

/**
 * Skeleton row for list-style layouts: avatar circle + two text lines.
 * Wrap several in a container to mimic a loading list.
 */
export function SkeletonRow({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-start gap-3 px-4 py-3', className)}>
      <Skeleton className="size-9 shrink-0 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-3/5" />
        <Skeleton className="h-3 w-4/5" />
      </div>
    </div>
  );
}

/**
 * Skeleton card matching KPI / stat card layouts.
 */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-white/30 bg-white/75 p-4 backdrop-blur-2xl',
        className,
      )}
    >
      <Skeleton className="mb-2 h-3 w-16" />
      <Skeleton className="mb-2 h-6 w-20" />
      <Skeleton className="h-3 w-24" />
    </div>
  );
}

/**
 * Skeleton list: renders `count` SkeletonRows inside a bordered container.
 */
export function SkeletonList({
  count = 5,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'divide-y divide-white/20 rounded-xl border border-white/30 bg-white/75 backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.04)]',
        className,
      )}
    >
      {Array.from({ length: count }, (_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  );
}

/**
 * Wrapper that shows skeleton content while loading, then the real content.
 *
 * @example
 * <DataLoader loading={query.isLoading} skeleton={<SkeletonList count={3} />}>
 *   <RealContent data={query.data} />
 * </DataLoader>
 */
export function DataLoader({
  loading,
  skeleton,
  children,
}: {
  loading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}) {
  return loading ? <>{skeleton}</> : <>{children}</>;
}
